import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";

const SalesRevenueExpenseChart = ({ data }) => {
  const { reports = [], grossSalesPerDay = [] } = data || {};
  const [view, setView] = useState("ALL");

  const allSeries = useMemo(() => {
    if (!reports.length && !grossSalesPerDay.length) return null;

    const dateMap = {};

    reports.forEach((doc) => {
      const key = dayjs(doc.date).format("YYYY-MM-DD");
      if (!dateMap[key])
        dateMap[key] = {
          gross: 0,
          grossNoVoid: 0,
          net: 0,
          netNoVoid: 0,
          expense: 0,
          birNetSales: 0,
          birNetSalesNoVoid: 0,
        };

      const isVoided = doc.entries.some(
        (e) =>
          e.category === "Sales" &&
          e.subcategory === "Expense" &&
          typeof e.name === "string" &&
          e.name.toLowerCase().includes("void")
      );

      doc.entries.forEach((e) => {
        const amount = e.originalPrice ?? e.price ?? 0;
        const qty = e.amount || 0;

        if (e.subcategory === "Income") {
          const sales = amount * qty;
          dateMap[key].net += sales;
          if (!isVoided) dateMap[key].netNoVoid += sales;
        } else if (["Expense", "Deduction"].includes(e.subcategory)) {
          const cost = e.subcategory === "Deduction" ? amount * qty : amount;
          dateMap[key].expense += cost;
        }
      });
    });

    grossSalesPerDay.forEach(({ date, grossSales, grossNoVoid, birNetSales, birNetSalesNoVoid }) => {
      if (!dateMap[date])
        dateMap[date] = {
          gross: 0,
          grossNoVoid: 0,
          net: 0,
          netNoVoid: 0,
          expense: 0,
          birNetSales: 0,
          birNetSalesNoVoid: 0,
        };

      dateMap[date].gross = grossSales;
      dateMap[date].grossNoVoid = grossNoVoid ?? grossSales;

      dateMap[date].net = +((dateMap[date].gross * 100) / 112).toFixed(2);
      dateMap[date].netNoVoid = +(
        (dateMap[date].grossNoVoid * 100) /
        112
      ).toFixed(2);

      dateMap[date].birNetSales = birNetSales;
      dateMap[date].birNetSalesNoVoid = birNetSalesNoVoid;
    });

    const labels = Object.keys(dateMap).sort();
    const build = (field) => labels.map((d) => dateMap[d][field]);

    return {
      labels,
      series: {
        gross: build("gross"),
        grossNoVoid: build("grossNoVoid"),
        net: build("net"),
        netNoVoid: build("netNoVoid"),
        expense: build("expense"),
        birNetSales: build("birNetSales"),
        birNetSalesNoVoid: build("birNetSalesNoVoid"),
      },
    };
  }, [reports, grossSalesPerDay]);

  const chartData = useMemo(() => {
    if (!allSeries) return null;
    const { labels, series } = allSeries;

    const common = {
      expense: {
        label: "Expense",
        data: series.expense,
        backgroundColor: "#F44336",
      },
    };

    const datasetsByView = {
      ALL: [
        {
          label: "Gross Sales",
          data: series.gross,
          backgroundColor: "#2196F3",
        },
        {
          label: "Gross Sales (no void)",
          data: series.grossNoVoid,
          backgroundColor: "#64B5F6",
        },
        {
          label: "Net Sales w/o VAT",
          data: series.net,
          backgroundColor: "#4CAF50",
        },
        {
          label: "Net Sales w/o VAT (no void)",
          data: series.netNoVoid,
          backgroundColor: "#81C784",
        },
        common.expense,
      ],
      WITH_VOID: [
        {
          label: "Gross Sales",
          data: series.gross,
          backgroundColor: "#2196F3",
        },
        {
          label: "Net Sales w/o VAT",
          data: series.net,
          backgroundColor: "#4CAF50",
        },
        common.expense,
      ],
      WITHOUT_VOID: [
        {
          label: "Gross Sales (no void)",
          data: series.grossNoVoid,
          backgroundColor: "#64B5F6",
        },
        {
          label: "Net Sales w/o VAT (no void)",
          data: series.netNoVoid,
          backgroundColor: "#81C784",
        },
        common.expense,
      ],
      BIR: [
        {
          label: "Gross Sales",
          data: series.gross,
          backgroundColor: "#2196F3",
        },
        {
          label: "Gross Sales (no void)",
          data: series.grossNoVoid,
          backgroundColor: "#64B5F6",
        },
        {
          label: "BIR Net Sales",
          data: series.birNetSales,
          backgroundColor: "#4CAF50",
        },
        {
          label: "BIR Net Sales (no void)",
          data: series.birNetSalesNoVoid,
          backgroundColor: "#81C784",
        },
        common.expense,
      ],
    };

    return {
      labels,
      datasets: datasetsByView[view],
    };
  }, [allSeries, view]);

  if (!chartData) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="sales-view-label">View</InputLabel>
          <Select
            labelId="sales-view-label"
            value={view}
            label="View"
            onChange={(e) => setView(e.target.value)}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="WITH_VOID">With Void</MenuItem>
            <MenuItem value="WITHOUT_VOID">Without Void</MenuItem>
            <MenuItem value="BIR">BIR Net Sales</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Bar
        data={chartData}
        options={{
          responsive: true,
          plugins: {
            title: { display: true, text: "Revenue and Expense Over Time" },
            tooltip: {
              callbacks: {
                label: (ctx) =>
                  `${ctx.dataset.label}: ₱${(ctx.raw || 0).toLocaleString()}`,
              },
            },
          },
          scales: {
            y: {
              ticks: {
                callback: (v) => `₱${v.toLocaleString()}`,
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default SalesRevenueExpenseChart;
